import { stripLocalePrefix } from '~/constants/appLocale'
import { COUNTRY_CODES } from '~/constants/countries'

/**
 * Affiliate program terms. Every place that shows or computes a commission
 * reads these numbers; nothing is repeated in components or translations.
 */
export const AFFILIATE = {
  /** Share of each paid order (without tax) the affiliate earns. */
  commissionRate: 0.5,
  /**
   * Days a commission waits before it can be paid out. It covers the refund
   * window: a refunded order cancels its commission, a paid one cannot.
   */
  holdDays: 30,
  /** Smallest balance paid out, in cents. */
  minPayoutCents: 2000,
  /** How long a click on a link is remembered before a purchase, in days. */
  cookieDays: 60,
  /** Name of the cookie holding the referring code. */
  cookieName: 'imr_ref',
  /** Query parameter that carries the code: https://inmyresume.com/?ref=code */
  queryParam: 'ref',
  /**
   * Clicks counted per visitor (hashed IP) and day. More are ignored, so
   * reloading a link cannot inflate a partner's statistics.
   */
  clicksPerIpPerDay: 20,
  /** Notice given before new terms or the end of a membership take effect, in days. */
  noticeDays: 14,
} as const

/**
 * The discount a partner's own code gives the buyer on one payment (a
 * subscription's first), in percent. The partner picks it, up to `max`, in
 * steps of `step`; the commission is a share of what is paid after it, so a
 * bigger discount costs the partner too. An admin can raise one partner's
 * limit up to `ceiling` (affiliates.max_discount_percent).
 */
export const AFFILIATE_DISCOUNT = {
  min: 5,
  max: 30,
  ceiling: 50,
  step: 5,
  /** Preselected for a new code. */
  default: 20,
} as const

function percentSteps(from: number, to: number): number[] {
  return Array.from({ length: (to - from) / AFFILIATE_DISCOUNT.step + 1 }, (_, i) => from + i * AFFILIATE_DISCOUNT.step)
}

/** 5, 10, … up to the partner's limit: the discounts they can choose. */
export function discountOptions(max: number = AFFILIATE_DISCOUNT.max): number[] {
  return percentSteps(AFFILIATE_DISCOUNT.min, max)
}

/** 5, 10, … 30: the discounts a partner can choose without a raised limit. */
export const AFFILIATE_DISCOUNT_OPTIONS: readonly number[] = discountOptions()

/** 30, 35, … 50: the limits an admin can give a partner. */
export const AFFILIATE_DISCOUNT_LIMITS: readonly number[] = percentSteps(AFFILIATE_DISCOUNT.max, AFFILIATE_DISCOUNT.ceiling)

/** A partner's limit from affiliates.max_discount_percent; empty or invalid means the usual one. */
export function maxDiscountFor(limit: number | null | undefined): number {
  return limit != null && AFFILIATE_DISCOUNT_LIMITS.includes(limit) ? limit : AFFILIATE_DISCOUNT.max
}

export function normalizeDiscountPercent(raw: unknown, max: number = AFFILIATE_DISCOUNT.max): number | null {
  const percent = Number(raw)
  return discountOptions(max).includes(percent) ? percent : null
}

/**
 * A partner's discount code, e.g. JANA20: what LemonSqueezy accepts (capital
 * letters and digits), 4–20 characters so it can be said and typed easily.
 */
export const AFFILIATE_DISCOUNT_CODE_PATTERN = /^[A-Z0-9]{4,20}$/

export function normalizeDiscountCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const code = raw.trim().toUpperCase().replace(/[\s-]+/g, '')
  return AFFILIATE_DISCOUNT_CODE_PATTERN.test(code) ? code : null
}

/** A first suggestion for a partner's code: their link code and the discount, "jana-novak", 20 → "JANANOVAK20". */
export function suggestDiscountCode(linkCode: string, percent: number = AFFILIATE_DISCOUNT.default): string {
  const base = linkCode.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 18)
  return `${base}${percent}`.slice(0, 20)
}

/** Lowercase letters, digits and dashes, 3–32 characters, not starting with a dash. */
export const AFFILIATE_CODE_PATTERN = /^[a-z0-9][a-z0-9-]{2,31}$/

export function normalizeAffiliateCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const code = raw.trim().toLowerCase()
  return AFFILIATE_CODE_PATTERN.test(code) ? code : null
}

/**
 * The words a link code is made from: the name the account has (Google sends
 * one), else the e-mail's name part. "Jana Nováková" → "jana-novakova".
 */
export function affiliateCodeBase(name: unknown, email: string | undefined): string {
  const slug = (text: string) => text
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .toLowerCase()
    // Letters that do not decompose into a base letter and an accent.
    .replace(/ł/g, 'l')
    .replace(/ß/g, 'ss')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24)
    .replace(/-+$/, '')
  const fromName = typeof name === 'string' ? slug(name) : ''
  return fromName.length >= 3 ? fromName : slug(email?.split('@')[0] ?? '')
}

export function commissionFor(amountCents: number): number {
  return Math.floor(Math.max(0, amountCents) * AFFILIATE.commissionRate)
}

/**
 * Version of the affiliate terms (constants/legal/affiliateTerms.ts). Bump it
 * whenever the text changes: members are asked to accept the new version, and
 * payouts wait until they do.
 */
export const AFFILIATE_TERMS_VERSION = '2026-11-16'

/**
 * When AFFILIATE_TERMS_VERSION takes effect for existing members. Set it at
 * least AFFILIATE.noticeDays after the change is announced (admin: "E-mail
 * partners about new terms"); until then payouts continue under the old
 * version.
 */
export const AFFILIATE_TERMS_EFFECTIVE = '2026-11-16'

/**
 * The partner's own page, /p/<code> (/cs/p/<code>, /de/p/<code>): their name
 * and a few words of their own above what the site does, and the discount a
 * buyer they bring gets. Short enough to say aloud or print, so it is also
 * the link the dashboard offers first.
 */
export const AFFILIATE_PAGE_PREFIX = '/p/'

/**
 * The widget partners embed with an iframe, /embed/<code>. It runs on their
 * site, so it shows no cookie banner and records no visit of its own; only
 * a click through to us counts (plugins/analytics.client.ts, app.vue).
 */
export const AFFILIATE_EMBED_PREFIX = '/embed/'

/** What a partner may write on their page, checked on the server and the form. */
export const AFFILIATE_PROFILE = {
  nameMax: 60,
  messageMax: 280,
} as const

/** "/cs/embed/jana" → true. Takes a path in any language. */
export function isAffiliateEmbedPath(path: string): boolean {
  return stripLocalePrefix(path).startsWith(AFFILIATE_EMBED_PREFIX)
}

/**
 * Pages a partner can point their link at. `null` is their own page. Every
 * path here is a marketing page, so the link opens in the language chosen.
 */
export const AFFILIATE_LINK_TARGETS = [
  { id: 'partnerPage', path: null },
  { id: 'home', path: '/' },
  { id: 'builder', path: '/free-cv-builder' },
  { id: 'atsCheck', path: '/ats-check' },
  { id: 'coverLetter', path: '/ai-cover-letter' },
  { id: 'templates', path: '/templates' },
  { id: 'tailor', path: '/tailor-resume-to-job' },
  { id: 'pricing', path: '/pricing' },
] as const

export type AffiliateLinkTargetId = typeof AFFILIATE_LINK_TARGETS[number]['id']

/** Networks the dashboard shares to in one click; each is its own ?src=share-<id>. */
export const AFFILIATE_SHARE_NETWORKS = ['linkedin', 'facebook', 'whatsapp', 'x', 'telegram', 'email'] as const
export type AffiliateShareNetwork = typeof AFFILIATE_SHARE_NETWORKS[number]

/**
 * The address that opens a network's share dialog with the link (and, where
 * the network takes one, the text). LinkedIn and Facebook read only the link;
 * the dashboard copies the text for the partner to paste.
 */
export function shareUrl(network: AffiliateShareNetwork, link: string, text: string, subject = ''): string {
  const u = encodeURIComponent(link)
  const t = encodeURIComponent(text)
  switch (network) {
    case 'linkedin': return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`
    case 'facebook': return `https://www.facebook.com/sharer/sharer.php?u=${u}`
    case 'whatsapp': return `https://wa.me/?text=${encodeURIComponent(`${text} ${link}`)}`
    case 'x': return `https://twitter.com/intent/tweet?text=${t}&url=${u}`
    case 'telegram': return `https://t.me/share/url?url=${u}&text=${t}`
    case 'email': return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${text}\n\n${link}`)}`
  }
}

/**
 * Adds the partner's code (and a source tag) to a link on our site. Accepts a
 * path or a full URL on our host; anything else is refused with null, so a
 * partner cannot turn the builder into a redirect to someone else's site.
 */
export function withAffiliateRef(target: string, code: string, siteUrl: string, source = ''): string | null {
  let url: URL
  const site = new URL(siteUrl)
  try {
    url = new URL(target.trim(), site)
  } catch {
    return null
  }
  const bare = (host: string) => host.replace(/^www\./, '')
  if (!/^https?:$/.test(url.protocol) || bare(url.hostname) !== bare(site.hostname)) return null
  url.protocol = site.protocol
  url.host = site.host
  url.searchParams.set(AFFILIATE.queryParam, code)
  if (source) url.searchParams.set('src', source)
  else url.searchParams.delete('src')
  return url.toString()
}

/**
 * Banners partners can embed, rendered to public/affiliate-banners/<id>.png
 * (and <id>@2x.png) by scripts/build-affiliate-banners.mjs, which reads this
 * literal. Keep it plain JSON-like data.
 */
export const AFFILIATE_BANNERS = [
  { id: 'leaderboard-728x90', width: 728, height: 90, layout: 'strip' },
  { id: 'mobile-320x50', width: 320, height: 50, layout: 'strip' },
  { id: 'rectangle-300x250', width: 300, height: 250, layout: 'box' },
  { id: 'rectangle-336x280', width: 336, height: 280, layout: 'box' },
  { id: 'skyscraper-160x600', width: 160, height: 600, layout: 'tall' },
  { id: 'half-page-300x600', width: 300, height: 600, layout: 'tall' },
  { id: 'social-1200x628', width: 1200, height: 628, layout: 'social' },
  { id: 'square-1080x1080', width: 1080, height: 1080, layout: 'social' },
  { id: 'story-1080x1920', width: 1080, height: 1920, layout: 'story' },
] as const

/**
 * Languages the banners are rendered in: <id>.png in English, <id>-<locale>.png
 * otherwise. Keep it plain JSON-like data (scripts/build-affiliate-banners.mjs).
 */
export const AFFILIATE_BANNER_LOCALES = ['en', 'cs', 'de'] as const
export type AffiliateBannerLocale = typeof AFFILIATE_BANNER_LOCALES[number]

/** The image of a banner in a language: "/affiliate-banners/square-1080x1080-cs@2x.png". */
export function affiliateBannerPath(banner: Pick<AffiliateBanner, 'id'>, locale: AffiliateBannerLocale, retina = false): string {
  return `/affiliate-banners/${banner.id}${locale === 'en' ? '' : `-${locale}`}${retina ? '@2x' : ''}.png`
}

export type AffiliateBanner = typeof AFFILIATE_BANNERS[number]

/** Where a video's copy lives in affiliate.materials.video.items. */
export type AffiliateVideoKey = 'promo' | 'chat' | 'templates' | 'tailor' | 'interview' | 'letter'

export interface AffiliateVideo {
  /** The file's name, and the click source tag (?src=video-<id>[-<lang>]), so it stays put. */
  id: string
  key: AffiliateVideoKey
  width: number
  height: number
  /** One length, or one per language when the video has a cut in each (the typed text differs). */
  seconds: number | Record<AffiliateBannerLocale, number>
  /** A cut in every banner language: public/affiliate-videos/<id>-<lang>.mp4; otherwise <id>.mp4, in English. */
  localized: boolean
}

/**
 * Videos partners can post, in public/affiliate-videos/. Copied from doc/video/
 * (the source and its render live there): re-render, then copy again
 * (doc/video/features/publish.mjs does it for the feature reels).
 */
export const AFFILIATE_VIDEOS: readonly AffiliateVideo[] = [
  // doc/video/tiktok: the AI chat CV built in under five seconds. No sound; English UI.
  { id: 'inmyresume-ai-chat-cv', key: 'promo', width: 1080, height: 1920, seconds: 35, localized: false },
  // doc/video/features: one feature each, the 9:16 reel in English, Czech and German. No sound; captions in the picture.
  { id: 'inmyresume-chat-reel', key: 'chat', width: 1080, height: 1920, seconds: { en: 34, cs: 34, de: 34 }, localized: true },
  { id: 'inmyresume-templates-reel', key: 'templates', width: 1080, height: 1920, seconds: { en: 46, cs: 47, de: 46 }, localized: true },
  { id: 'inmyresume-tailor-reel', key: 'tailor', width: 1080, height: 1920, seconds: { en: 29, cs: 29, de: 29 }, localized: true },
  { id: 'inmyresume-interview-reel', key: 'interview', width: 1080, height: 1920, seconds: { en: 30, cs: 30, de: 31 }, localized: true },
  { id: 'inmyresume-letter-reel', key: 'letter', width: 1080, height: 1920, seconds: { en: 25, cs: 25, de: 26 }, localized: true },
]

/** The cut of a video in a language, and its click source tag. */
export function affiliateVideoCut(video: AffiliateVideo, lang: AffiliateBannerLocale) {
  const cutLang = video.localized ? lang : 'en'
  const name = video.localized ? `${video.id}-${cutLang}` : video.id
  return {
    lang: cutLang,
    file: `${name}.mp4`,
    src: `/affiliate-videos/${name}.mp4`,
    source: `video-${name}`,
    seconds: typeof video.seconds === 'number' ? video.seconds : video.seconds[cutLang],
  }
}

/** Source tag for clicks (?src=…): letters, digits, dashes and underscores, up to 40 characters. */
export function normalizeAffiliateSource(raw: unknown): string {
  if (typeof raw !== 'string') return ''
  const source = raw.trim().toLowerCase()
  return /^[a-z0-9_-]{1,40}$/.test(source) ? source : ''
}

export const PAYOUT_METHODS = ['paypal', 'wise', 'bank'] as const
export type PayoutMethod = typeof PAYOUT_METHODS[number]

/**
 * What a partner must give us before the first payout: who they are (for the
 * payout record and, for a business, the invoice), where they live for tax,
 * and where the money goes. Stored as affiliates.payout_details.
 */
export interface PayoutDetails {
  entityType: 'individual' | 'business'
  /** Full legal name, or the registered company name for a business. */
  legalName: string
  street: string
  city: string
  postalCode: string
  /** ISO 3166-1 alpha-2, e.g. CZ. Also the country of tax residence. */
  country: string
  /** Company registration number (IČO). Required for a business. */
  companyId?: string
  /** VAT number, when registered for VAT. */
  vatId?: string
  method: PayoutMethod
  /** PayPal or Wise account e-mail. */
  payoutEmail?: string
  iban?: string
  /** BIC / SWIFT of the bank. */
  swift?: string
}

export type PayoutDetailsField = keyof PayoutDetails

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const IBAN = /^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/
const SWIFT = /^[A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/

function text(value: unknown, max = 200): string {
  return typeof value === 'string' ? value.trim().slice(0, max) : ''
}

/**
 * Cleans a payout form and lists the fields that are missing or invalid.
 * Shared by the form and the server, so both agree on what "complete" means.
 */
export function validatePayoutDetails(raw: unknown): { details: PayoutDetails, errors: PayoutDetailsField[] } {
  const input = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const method = PAYOUT_METHODS.includes(input.method as PayoutMethod) ? input.method as PayoutMethod : 'paypal'
  const details: PayoutDetails = {
    entityType: input.entityType === 'business' ? 'business' : 'individual',
    legalName: text(input.legalName),
    street: text(input.street),
    city: text(input.city, 100),
    postalCode: text(input.postalCode, 20),
    country: text(input.country, 2).toUpperCase(),
    companyId: text(input.companyId, 40) || undefined,
    vatId: text(input.vatId, 40).toUpperCase().replace(/\s+/g, '') || undefined,
    method,
    payoutEmail: method === 'bank' ? undefined : text(input.payoutEmail, 254) || undefined,
    iban: method === 'bank' ? text(input.iban, 40).toUpperCase().replace(/\s+/g, '') || undefined : undefined,
    swift: method === 'bank' ? text(input.swift, 11).toUpperCase().replace(/\s+/g, '') || undefined : undefined,
  }

  const errors: PayoutDetailsField[] = []
  if (details.legalName.length < 2) errors.push('legalName')
  if (!details.street) errors.push('street')
  if (!details.city) errors.push('city')
  if (!details.postalCode) errors.push('postalCode')
  if (!(COUNTRY_CODES as readonly string[]).includes(details.country)) errors.push('country')
  if (details.entityType === 'business' && !details.companyId) errors.push('companyId')
  if (details.vatId && !/^[A-Z]{2}[A-Z0-9]{2,13}$/.test(details.vatId)) errors.push('vatId')
  if (method !== 'bank' && !EMAIL.test(details.payoutEmail ?? '')) errors.push('payoutEmail')
  if (method === 'bank' && !IBAN.test(details.iban ?? '')) errors.push('iban')
  if (method === 'bank' && !SWIFT.test(details.swift ?? '')) errors.push('swift')

  return { details, errors }
}

export function isPayoutReady(raw: unknown): boolean {
  return !!raw && validatePayoutDetails(raw).errors.length === 0
}

/**
 * LemonSqueezy's own affiliate program runs next to ours: its partners link
 * with ?aff=… and lmsqueezy.com/affiliate.js (nuxt.config.ts) remembers them.
 * A checkout created through the API only credits them when its URL carries
 * ?aff_ref=<tracking id>, so the server adds it (create-checkout-session).
 *
 * One order pays one program: the one whose link the buyer clicked last. The
 * click times are kept in httpOnly cookies (server/utils/affiliateCookie.ts),
 * set only with cookie consent, like the referral cookie itself.
 */
export const LEMON_AFFILIATE = {
  /** Query parameter of LemonSqueezy affiliate links. */
  queryParam: 'aff',
  /** Query parameter LemonSqueezy reads on the checkout URL. */
  checkoutParam: 'aff_ref',
  /** When the visitor last came through a LemonSqueezy affiliate link (ms). */
  clickCookie: 'imr_ls_at',
  /** custom_data.affiliate_program on orders credited to LemonSqueezy. */
  program: 'lemonsqueezy',
} as const

/**
 * From this day a LemonSqueezy click can win over an account's stored
 * referral to one of our partners (affiliate terms 2026-10-13, section 3).
 * Before it, the earlier terms promised such accounts' purchases to our
 * partner, so a LemonSqueezy affiliate only gets buyers nobody of ours has.
 */
export const LEMON_LAST_CLICK_FROM = '2026-10-13'

/** When the visitor last came through one of our affiliate links (ms). */
export const AFFILIATE_CLICK_COOKIE = 'imr_ref_at'

/** LemonSqueezy's tracking id (LemonSqueezy.Affiliate.GetID()), or null. */
export function normalizeLemonAffiliateRef(raw: unknown): string | null {
  if (typeof raw !== 'string' && typeof raw !== 'number') return null
  const ref = String(raw).trim()
  return /^[A-Za-z0-9_-]{1,64}$/.test(ref) ? ref : null
}

/**
 * Affonso (affonso.io) runs a third affiliate program next to ours and
 * LemonSqueezy's. Its pixel (nuxt.config.ts) remembers the partner in the
 * `affonso_referral` cookie once the visitor allows cookies
 * (plugins/affiliate.client.ts); the checkout passes that id through
 * custom_data, and the webhook reports the paid order to Affonso's API, which
 * calculates and pays the commission itself (server/utils/affonso.ts).
 */
export const AFFONSO_AFFILIATE = {
  /** Cookie the Affonso pixel stores the referral id in, readable by the server. */
  referralCookie: 'affonso_referral',
  /** Second cookie the pixel sets (partner data, coupons). */
  dataCookie: 'affonso_data',
  /**
   * When the visitor last came through an Affonso link (ms), dated when the
   * pixel reports a tracked click. Affonso links should use ?via= or ?atp=:
   * the pixel also reads ?ref and ?aff, which are our and LemonSqueezy's.
   */
  clickCookie: 'imr_af_at',
  /** custom_data.affiliate_program on orders credited to Affonso. */
  program: 'affonso',
} as const

/**
 * From this day an Affonso click can win over one of our partners, like a
 * LemonSqueezy click does from LEMON_LAST_CLICK_FROM. The affiliate terms
 * (section 3) name only LemonSqueezy, so until they also name Affonso and the
 * change has taken effect this stays null: a buyer with one of our partners
 * keeps paying that partner, and Affonso gets only buyers nobody of ours has.
 */
export const AFFONSO_LAST_CLICK_FROM: string | null = null

/** The Affonso referral id from its cookie, or null. */
export function normalizeAffonsoReferral(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const ref = raw.trim()
  return /^[A-Za-z0-9_-]{1,64}$/.test(ref) ? ref : null
}

export type CheckoutProgram = 'own' | 'lemon' | 'affonso'

type Touch = { at: number } | null | undefined

/** Whether the terms in force let `program` take a buyer from one of our partners. */
function mayOverrideOwn(program: Exclude<CheckoutProgram, 'own'>, now: Date): boolean {
  const from = program === 'lemon' ? LEMON_LAST_CLICK_FROM : AFFONSO_LAST_CLICK_FROM
  return !!from && now >= new Date(`${from}T00:00:00Z`)
}

/**
 * Which program an order pays: the later click wins. A program whose click
 * may not yet beat one of our partners (see the *_LAST_CLICK_FROM dates)
 * drops out while there is one. A touch without a known time (at = 0, e.g. a
 * cookie from before click times were kept) loses to any dated one; a tie
 * goes to our own program, whose referral lasts, then to LemonSqueezy.
 */
export function pickCheckoutProgram(
  touches: { own?: Touch, lemon?: Touch, affonso?: Touch },
  now: Date = new Date(),
): CheckoutProgram | null {
  let best: CheckoutProgram | null = null
  let bestAt = -1
  for (const program of ['own', 'lemon', 'affonso'] as const) {
    const touch = touches[program]
    if (!touch) continue
    if (program !== 'own' && touches.own && !mayOverrideOwn(program, now)) continue
    if (touch.at > bestAt) {
      best = program
      bestAt = touch.at
    }
  }
  return best
}

/** Adds LemonSqueezy's ?aff_ref=… to a checkout URL, keeping its other parameters. */
export function withLemonAffiliateRef(checkoutUrl: string, ref: string): string {
  const url = new URL(checkoutUrl)
  url.searchParams.set(LEMON_AFFILIATE.checkoutParam, ref)
  return url.toString()
}
